import { type PanelManager, type PanelKeys } from '@databases/panel-types';
import { container } from '@databases/service-container';
import { type DatabaseBridgeAPI, getBridgeAPI } from '@databases/utils/database/bridge';
import React, {
  createContext,
  useState,
  type PropsWithChildren,
  type FC,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { useSnapshot } from 'valtio';

import { currentDrawerPanel } from '@canvas/sidebar-panel-state';

export type PanelsType = {
  [key in PanelKeys]?: React.ReactNode;
};

export type ShowPanelType = PanelManager['showPanel'];

export interface PanelContextInterface {
  showPanel: PanelManager['showPanel'];
  hidePanel: PanelManager['hidePanel'];
}

export const PanelContext = createContext<PanelContextInterface>({
  showPanel: () => {},
  hidePanel: () => {}
});

/**
 * @deprecated This is actively being migrated away from in favor the PanelManager
 * which has state that is accessible both inside and outside of React.
 */
export const PanelProvider: FC<PropsWithChildren> = ({ children }) => {
  const [bridgeApi, setBridgeApi] = useState<DatabaseBridgeAPI | null>(null);
  const panelManager = container.resolve('PanelManager');
  const { panel: currentPanel } = useSnapshot(panelManager.state);

  /**
   * The following two useEffect will be later moved to a dedicated
   * listener on the panel state rather than here in the context. As the context will be removed entirely
   * now the the panel state has been extracted.
   */
  useEffect(() => {
    const loadBridgeApi = async () => {
      await getBridgeAPI().then(setBridgeApi);
    };
    loadBridgeApi();
  }, []);

  useEffect(() => {
    if (bridgeApi?.objectSidebarMethods) {
      // Navigation between comments in Confluence panel may open entry view panel
      if (
        currentPanel === 'EntryViewPanel' &&
        currentDrawerPanel.panel === 'commentsPanel'
      ) {
        return;
      }

      if (currentPanel !== null) {
        bridgeApi.objectSidebarMethods.hideObjectSidebarPanel?.();
        bridgeApi.objectSidebarMethods.updateObjectSidebarControlState?.(false);
      } else {
        bridgeApi.objectSidebarMethods.updateObjectSidebarControlState?.(true);
      }
    }
  }, [bridgeApi, currentPanel]);

  const showPanel: ShowPanelType = useCallback(
    (panelKey, data) => {
      panelManager.showPanel(panelKey, data);
    },
    [panelManager]
  );

  const hidePanel = useCallback(() => {
    panelManager.hidePanel();
  }, [panelManager]);

  const contextValue = useMemo(
    () => ({
      showPanel,
      hidePanel
    }),
    [showPanel, hidePanel]
  );

  return <PanelContext.Provider value={contextValue}>{children}</PanelContext.Provider>;
};
